import { X, Plus, Minus } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { getShipmentByReference, cambiarEstatusOrden, cambiarEstatusEmbarque, actualizarReentregaOrden } from '../../api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';

const ModalEntrega = ({ onClose, referencia: initialReferencia, shipment: initialShipment, refreshShipments }) => {
  const [referencia, setReferencia] = useState(initialReferencia || '');
  const [embarque, setEmbarque] = useState(initialShipment || { orders: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [embarqueEncontrado, setEmbarqueEncontrado] = useState(!!initialShipment);
  const [products, setProducts] = useState([]);
  const [motivos, setMotivos] = useState([]);
  const [ordenesEstatus, setOrdenesEstatus] = useState({});
  const [valoresSelectores, setValoresSelectores] = useState({});
  const [cantidadNoEntregada, setCantidadNoEntregada] = useState({});
  const [motivoNoEntregado, setMotivoNoEntregado] = useState({});
  const [currentStop, setCurrentStop] = useState(1);
  const [showReentregaModal, setShowReentregaModal] = useState(false);

  const validarRechazoParcial = (orden) => {
    if (valoresSelectores[orden.Order_number]?.Substatus !== 'PARCIAL') {
      return true;
    }
    return (orden.productos || []).some(producto => {
      const rechazos = cantidadNoEntregada[producto.product_id] || [];
      // Ahora validamos que haya al menos un rechazo con cantidad > 0 y motivo no vacío
      return rechazos.some(r => r.cantidad > 0 && r.motivo);
    });
  };

  const esParcialPeroRechazoTotal = (orden) => {
    if (valoresSelectores[orden.Order_number]?.Substatus !== 'PARCIAL') return false;
    // Suma de piezas rechazadas por todos los motivos de todos los productos
    const totalPiezas = (orden.productos || []).reduce((acc, prod) => acc + (prod.product_qt || 0), 0);
    const totalRechazadas = (orden.productos || []).reduce((acc, prod) => {
      const rechazos = cantidadNoEntregada[prod.product_id] || [];
      return acc + rechazos.reduce((a, r) => a + (r.cantidad ? Number(r.cantidad) : 0), 0);
    }, 0);
    return totalPiezas > 0 && totalPiezas === totalRechazadas;
  };

  useEffect(() => {
    if (initialShipment && initialShipment.orders) {
      setEmbarque(initialShipment);
      setEmbarqueEncontrado(true);

      const initialStatus = {};
      const initialSelectores = {};
      initialShipment.orders.forEach((order) => {
        initialStatus[order.Order_number] = {
          status: order.status || 'PENDIENTE',
          Substatus: order.Substatus || 'PENDIENTE',
        };
        initialSelectores[order.Order_number] = {
          Substatus: order.Substatus || 'PENDIENTE',
        };
      });
      setOrdenesEstatus(initialStatus);
      setValoresSelectores(initialSelectores);

      const nuevaParada = determinarParadaActual(initialShipment.orders);
      setCurrentStop(nuevaParada || 1);
    }
  }, [initialShipment]);

  const determinarParadaActual = (orders) => {
    for (let stop = 1; stop <= 3; stop++) {
      const ordenesStop = orders.filter((order) => order.Stop === stop);
      const todasFinalizadas = ordenesStop.every(
        (order) => order.Order_status === 'FINALIZADO'
      );
      if (!todasFinalizadas) {
        return stop;
      }
    }
    return null;
  };

  const confirmarCambios = async () => {
    console.log('Confirmando cambios con la siguiente información:');
    console.log('Cantidad no entregada:', cantidadNoEntregada);
    console.log('Motivo no entregado:', motivoNoEntregado);

    const ordenesParadaActual = embarque.orders.filter(
      (order) => order.Stop === currentStop
    );

    const ordenSinRechazo = ordenesParadaActual.find(orden => {
      const subestatus = valoresSelectores[orden.Order_number]?.Substatus;
      return subestatus === 'PARCIAL' && !validarRechazoParcial(orden);
    });

    const ordenParcialComoTotal = ordenesParadaActual.find(orden => esParcialPeroRechazoTotal(orden));
    if (ordenParcialComoTotal) {
      toast.error(`La orden ${ordenParcialComoTotal.Order_number} está marcada como ENTREGA PARCIAL pero el total de piezas rechazadas equivale a un RECHAZO TOTAL. Por favor, marque la orden como RECHAZO TOTAL.`);
      return;
    }

    // Validación para RECHAZO TOTAL sin motivo (confirmarCambios)
    const ordenRechazoSinMotivo = ordenesParadaActual.find(orden => {
      const subestatus = valoresSelectores[orden.Order_number]?.Substatus;
      return subestatus === 'RECHAZADO' && (!motivoNoEntregado[orden.Order_number] || motivoNoEntregado[orden.Order_number].trim() === '');
    });
    if (ordenRechazoSinMotivo) {
      toast.error(`La orden ${ordenRechazoSinMotivo.Order_number} está marcada como RECHAZO TOTAL pero no se ha seleccionado un motivo. Por favor, seleccione un motivo de rechazo.`);
      return;
    }

    // Validación para órdenes en PENDIENTE (confirmarCambios)
    const ordenPendiente = ordenesParadaActual.find(orden => {
      const subestatus = valoresSelectores[orden.Order_number]?.Substatus || 'PENDIENTE';
      return subestatus === 'PENDIENTE';
    });
    if (ordenPendiente) {
      toast.error(`La orden ${ordenPendiente.Order_number} está en PENDIENTE. Por favor, seleccione un estatus para esa orden antes de finalizar.`);
      return;
    }

    if (ordenSinRechazo) {
      toast.error(`La orden ${ordenSinRechazo.Order_number} está marcada como PARCIAL pero no tiene piezas rechazadas.`);
      return;
    }

    try {
      for (const orden of ordenesParadaActual) {
        const doKey = orden.Order_number;
        const subestatus = valoresSelectores[doKey]?.Substatus || 'PENDIENTE';

        let productosAfectados = [];
        if (subestatus === "PARCIAL") {
          productosAfectados = (orden.productos || []).flatMap((producto) => {
            const rechazos = cantidadNoEntregada[producto.product_id] || [];
            // Enviar todos los rechazos válidos (cantidad > 0 y motivo no vacío), incluyendo la operación si existe
            return rechazos
              .filter(r => r.cantidad > 0 && r.motivo)
              .map((rechazo) => {
                // Buscar el objeto motivo para obtener la operación
                const motivoObj = motivos.find(m => m.descripcion === rechazo.motivo);
                return {
                  product_id: producto.product_id,
                  product_qt: producto.product_qt,
                  piezas_rechazadas: rechazo.cantidad,
                  motivo_rechazo: rechazo.motivo,
                  operacion: motivoObj && motivoObj.operacion ? motivoObj.operacion : undefined
                };
              });
          });
        } else if (subestatus === "RECHAZADO") {
          const motivoRechazo = motivoNoEntregado[doKey] || "";
          const motivoObj = motivos.find(m => m.descripcion === motivoRechazo);
          // Si el motivo es FALTANTE, no generar devolución
          if (motivoObj && motivoObj.operacion === 'FALTANTE') {
            productosAfectados = [];
          } else {
            productosAfectados = (orden.productos || []).map((producto) => {
              return {
                product_id: producto.product_id,
                product_qt: producto.product_qt,
                piezas_rechazadas: producto.product_qt,
                motivo_rechazo: motivoRechazo,
                operacion: motivoObj && motivoObj.operacion ? motivoObj.operacion : undefined
              };
            });
          }
        }

        const datos = {
          ordenId: orden.id,
          nuevoEstatus: "FINALIZADO",
          subestatus,
          productosAfectados,
        };

        console.log('Enviando datos al backend:', datos);

        await cambiarEstatusOrden(orden.id, datos);
      }

      toast.success('Los cambios han sido confirmados correctamente.');
      const todasFinalizadas = ordenesParadaActual.every(
        (order) => valoresSelectores[order.Order_number]?.Substatus || 'PENDIENTE'
      );
      console.log('Todas las órdenes finalizadas:', todasFinalizadas);

      if (todasFinalizadas) {
        const paradasTotales = Math.max(...embarque.orders.map((order) => order.Stop));
        const esUltimaParada = currentStop === paradasTotales;
        console.log('Es la última parada:', esUltimaParada);

        if (esUltimaParada) {
          try {
            const nuevoEmbarque = await getShipmentByReference(referencia);
            console.log('Nuevo embarque recibido:', nuevoEmbarque);
            console.log('Tipo de nuevoEmbarque:', typeof nuevoEmbarque);
            console.log('¿Es nuevoEmbarque un arreglo?', Array.isArray(nuevoEmbarque));

            if (!nuevoEmbarque) {
              console.error('No se recibió información del embarque');
              throw new Error('No se pudo obtener información del embarque');
            }

            let orders = [];
            if (Array.isArray(nuevoEmbarque)) {
              if (nuevoEmbarque.length > 0) {
                console.log('nuevoEmbarque[0]:', nuevoEmbarque[0]);
                orders = nuevoEmbarque[0]?.orders || [];
              }
            } else {
              orders = nuevoEmbarque?.orders || [];
            }
            
            console.log('Órdenes del embarque (extraído):', orders);

            if (!orders || orders.length === 0) {
              console.error('No se encontraron órdenes en el embarque');
              if (nuevoEmbarque && typeof nuevoEmbarque === 'object') {
                console.log('Propiedades del embarque:', Object.keys(nuevoEmbarque));
                
                if (Array.isArray(nuevoEmbarque)) {
                  for (let i = 0; i < nuevoEmbarque.length; i++) {
                    console.log(`Propiedades de nuevoEmbarque[${i}]:`, Object.keys(nuevoEmbarque[i]));
                    if (nuevoEmbarque[i].orders) {
                      orders = nuevoEmbarque[i].orders;
                      console.log(`Encontradas órdenes en nuevoEmbarque[${i}]:`, orders);
                      break;
                    }
                  }
                }
              }
            }

            const substatusOrdenes = (orders || []).map((order) => {
              return order.order_Substatus?.trim() || 'PENDIENTE';
            });
            console.log('Substatus de las órdenes actualizadas:', substatusOrdenes);

            const substatusValidos = substatusOrdenes.filter(
              (sub) => sub !== 'PENDIENTE' || substatusOrdenes.every((s) => s === 'PENDIENTE')
            );
            console.log('Substatus válidos:', substatusValidos);

            let substatusEmbarque = 'PENDIENTE';
            if (orders && orders.length === 1) {
              substatusEmbarque = substatusOrdenes[0] || 'PENDIENTE';
            } else if (orders && orders.length > 0) {
              if (substatusValidos.every((sub) => sub === 'ENTREGADO')) {
                substatusEmbarque = 'ENTREGADO';
              } else if (substatusValidos.every((sub) => sub === 'RECHAZADO')) {
                substatusEmbarque = 'RECHAZADO';
              } else if (substatusValidos.some((sub) => sub === 'PARCIAL' || sub === 'RECHAZADO')) {
                substatusEmbarque = 'PARCIAL';
              }
            }

            console.log('Substatus final del embarque:', substatusEmbarque);

            const respuestaEmbarque = await cambiarEstatusEmbarque(embarque.id, {
              status: 'FINALIZADO',
              Substatus: substatusEmbarque,
            });
            console.log('Respuesta al actualizar el embarque:', respuestaEmbarque);
            toast.success(`Embarque finalizado: Estado actualizado a ${substatusEmbarque}.`);
          } catch (error) {
            console.error('Error al actualizar el estado del embarque:', error);
            toast.error('Error al finalizar el embarque. Se guardaron los cambios de las órdenes.');
          }
        } else {
          await cambiarEstatusEmbarque(embarque.id, {
            status: 'TRANSITO',
            Substatus: 'EN RUTA',
          });
          toast.success('Embarque en progreso: Estado actualizado a TRANSITO.');
        }
      } else {
        toast.warning('Algunas órdenes no están finalizadas. Revise los estados.');
      }

      if (typeof refreshShipments === 'function') {
        refreshShipments();
      }
      
      onClose();
    } catch (error) {
      console.error('Error al confirmar los cambios:', error);
      toast.error('Hubo un error al confirmar los cambios.');
    }
  };

  const buscarEmbarque = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getShipmentByReference(referencia);
      if (response.length > 0) {
        const embarqueEncontrado = response[0];
        setEmbarque(embarqueEncontrado);
        setEmbarqueEncontrado(true);

        const initialStatus = {};
        const initialSelectores = {};
        embarqueEncontrado.orders.forEach((order) => {
          initialStatus[order.Order_number] = {
            status: order.status || 'PENDIENTE',
            Substatus: order.Substatus || 'PENDIENTE',
          };
          initialSelectores[order.Order_number] = {
            Substatus: order.Substatus || 'PENDIENTE',
          };
        });
        setOrdenesEstatus(initialStatus);
        setValoresSelectores(initialSelectores);

        const nuevaParada = determinarParadaActual(embarqueEncontrado.orders);
        setCurrentStop(nuevaParada || 1);

        toast.success('Embarque encontrado.');
      } else {
        setError('No se encontró el embarque.');
        setEmbarqueEncontrado(false);
      }
    } catch (error) {
      setError('Error al buscar el embarque.');
      setEmbarqueEncontrado(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (embarque.orders && embarque.orders.length > 0) {
      const nuevaParada = determinarParadaActual(embarque.orders);
      setCurrentStop(nuevaParada);
    }
  }, [ordenesEstatus, embarque.orders]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/productos`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching Products:', error);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchMotivos = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/motivos`);
        setMotivos(response.data);
      } catch (error) {
        console.error('Error fetching Motivos:', error);
      }
    };
    fetchMotivos();
  }, []);

  const iniciarReentrega = () => {
    setShowReentregaModal(true);
  };

  const guardarReentrega = async () => {
    const ordenesReentrega = embarque.orders.filter(order => order.substatus_reentrega === 'DESCARGANDO');
    
    const ordenSinRechazo = ordenesReentrega.find(orden => {
      const subestatus = valoresSelectores[orden.Order_number]?.Substatus;
      return subestatus === 'PARCIAL' && !validarRechazoParcial(orden);
    });

    const ordenParcialComoTotal = ordenesReentrega.find(orden => esParcialPeroRechazoTotal(orden));
    if (ordenParcialComoTotal) {
      toast.error(`La orden ${ordenParcialComoTotal.Order_number} está marcada como ENTREGA PARCIAL pero el total de piezas rechazadas equivale a un RECHAZO TOTAL. Por favor, marque la orden como RECHAZO TOTAL.`);
      return;
    }

    if (ordenSinRechazo) {
      toast.error(`La orden ${ordenSinRechazo.Order_number} está marcada como PARCIAL pero no tiene piezas rechazadas.`);
      return;
    }

    // Validación para RECHAZO TOTAL sin motivo (guardarReentrega)
    const ordenRechazoSinMotivoReentrega = ordenesReentrega.find(orden => {
      const subestatus = valoresSelectores[orden.Order_number]?.Substatus;
      return subestatus === 'RECHAZADO' && (!motivoNoEntregado[orden.Order_number] || motivoNoEntregado[orden.Order_number].trim() === '');
    });
    if (ordenRechazoSinMotivoReentrega) {
      toast.error(`La orden ${ordenRechazoSinMotivoReentrega.Order_number} está marcada como RECHAZO TOTAL pero no se ha seleccionado un motivo. Por favor, seleccione un motivo de rechazo.`);
      return;
    }

    // Validación para órdenes en PENDIENTE (guardarReentrega)
    const ordenPendienteReentrega = ordenesReentrega.find(orden => {
      const subestatus = valoresSelectores[orden.Order_number]?.Substatus || 'PENDIENTE';
      return subestatus === 'PENDIENTE';
    });
    if (ordenPendienteReentrega) {
      toast.error(`La orden ${ordenPendienteReentrega.Order_number} está en PENDIENTE. Por favor, seleccione un estatus para esa orden antes de finalizar.`);
      return;
    }

    try {
      const ordenesReentrega = embarque.orders.filter(order => order.substatus_reentrega === 'DESCARGANDO');
      for (const orden of ordenesReentrega) {
        const doKey = orden.Order_number;
        const subestatusReentrega = valoresSelectores[doKey]?.Substatus || 'PENDIENTE';

        let productosAfectados = [];
        if (subestatusReentrega === "ENTREGADO") {
          productosAfectados = (orden.productos || []).map((producto) => ({
            product_id: producto.product_id,
            product_qt: producto.product_qt,
            piezas_rechazadas: 0,
            motivo_rechazo: null,
          }));
        } else if (subestatusReentrega === "PARCIAL") {
          productosAfectados = (orden.productos || []).map((producto) => {
            const piezasRechazadas = cantidadNoEntregada[producto.product_id] || 0;
            const motivoRechazo = motivoNoEntregado[producto.product_id] || "";

            return {
              product_id: producto.product_id,
              product_qt: producto.product_qt,
              piezas_rechazadas: piezasRechazadas,
              motivo_rechazo: motivoRechazo,
            };
          });
        } else if (subestatusReentrega === "RECHAZADO") {
          productosAfectados = (orden.productos || []).map((producto) => ({
            product_id: producto.product_id,
            product_qt: producto.product_qt,
            piezas_rechazadas: producto.product_qt,
            motivo_rechazo: motivoNoEntregado[doKey] || "",
          }));
        }

        const datos = {
          ordenId: orden.id,
          subestatusReentrega,
          productosAfectados,
        };

        console.log('Enviando datos de reentrega al backend:', datos);

        await actualizarReentregaOrden(orden.id, datos);
      }

      toast.success('Reentrega guardada correctamente.');
      setShowReentregaModal(false);
    } catch (error) {
      console.error('Error al guardar la reentrega:', error);
      toast.error('Hubo un error al guardar la reentrega.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-auto relative">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
        >
          <X size={24} />
        </button>

        <h2 className="text-lg font-semibold mb-4">Buscar Embarque</h2>

        {!embarqueEncontrado && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Referencia del embarque</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={referencia}
                onChange={(e) => setReferencia(e.target.value)}
                className="mt-1 block w-full p-2 border rounded-md"
                placeholder="Ingrese la referencia"
              />
              <button
                onClick={buscarEmbarque}
                className="bg-sky-600 text-white p-2 rounded-md hover:bg-sky-700"
                disabled={loading}
              >
                {loading ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
          </div>
        )}

        {embarqueEncontrado && (
          <>
            <div className="mt-4 p-4 border rounded-md bg-gray-100">
              <h3 className="text-md font-semibold">Detalles del Embarque</h3>
              <div className="flex gap-6">
                <p><strong>Truck:</strong> {embarque.Truck}</p>
                <p><strong>Origen:</strong> {embarque.origin}</p>
                <p><strong>Destino:</strong> {embarque.destination}</p>
                <p><strong>Estado:</strong> {embarque.status}</p>
                <p><strong>Subestado:</strong> {embarque.Substatus}</p>
              </div>
            </div>

            {currentStop !== null && (
              <div className="mt-6 p-6 bg-gray-50 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">
                  {`Órdenes asociadas - Parada ${currentStop}`}
                </h3>
                {embarque.orders && embarque.orders.length > 0 ? (
                  <ul className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                    {embarque.orders
                      .filter((order) => order.Stop === currentStop)
                      .map((orden, index) => (
                        <li key={index} className="p-4 bg-white rounded-lg shadow-sm">
                          <div className="flex justify-between items-center">
                            <span className="font-medium"> {orden.Order_number}</span>
                            <select
                              value={valoresSelectores[orden.Order_number]?.Substatus || 'PENDIENTE'}
                              onChange={(e) =>
                                setValoresSelectores((prev) => ({
                                  ...prev,
                                  [orden.Order_number]: { Substatus: e.target.value },
                                }))
                              }
                              className="ml-2 p-2 border rounded-md"
                            >
                              <option value="PENDIENTE">PENDIENTE</option>
                              <option value="ENTREGADO">ENTREGA TOTAL</option>
                              <option value="PARCIAL">ENTREGA PARCIAL</option>
                              <option value="RECHAZADO">RECHAZO TOTAL</option>
                            </select>
                          </div>
                          {valoresSelectores[orden.Order_number]?.Substatus === 'PARCIAL' && (
                            <div className="mt-3 space-y-4">
                              {(orden.productos || []).map((producto, productIndex) => {
                                const productoDetalle = products.find(p => p.id === producto.product_id);
                                const codigoProducto = productoDetalle ? productoDetalle.codigo_producto : "Código no disponible";
                                const descripcionProducto = productoDetalle ? productoDetalle.descripcion : "Descripción no disponible";
                                if (!Array.isArray(cantidadNoEntregada[producto.product_id])) {
                                  cantidadNoEntregada[producto.product_id] = [{ cantidad: '', motivo: '' }];
                                }
                                return (
                                  <div key={productIndex} className="bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-200">
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2 justify-between">
                                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                        <span className="font-semibold text-blue-900">{codigoProducto}</span>
                                        <span className="text-gray-600 text-sm">{descripcionProducto}</span>
                                      </div>
                                      <span className="text-gray-500 text-xs sm:text-sm ml-auto">Total: {producto.product_qt}</span>
                                    </div>
                                    {(cantidadNoEntregada[producto.product_id] || []).map((rechazo, idx) => (
                                      <div key={idx} className="flex flex-col sm:flex-row gap-2 items-center mb-2 bg-white rounded-md p-2 border border-blue-100">
                                        <input
                                          type="number"
                                          min="0"
                                          max={(() => {
                                            // Sumar todas las cantidades de rechazo para este producto, excepto la actual
                                            const rechazos = cantidadNoEntregada[producto.product_id] || [];
                                            const sumaOtros = rechazos.reduce((acc, r, i2) => acc + (i2 !== idx && r.cantidad !== '' ? Number(r.cantidad) : 0), 0);
                                            // El máximo permitido para este input es el total menos lo ya asignado en otros motivos
                                            return Math.max(0, producto.product_qt - sumaOtros);
                                          })()}
                                          value={rechazo.cantidad}
                                          onChange={e => {
                                            let value = e.target.value === '' ? '' : Number(e.target.value);
                                            // Sumar todas las cantidades de rechazo para este producto, excepto la actual
                                            const rechazos = cantidadNoEntregada[producto.product_id] || [];
                                            const sumaOtros = rechazos.reduce((acc, r, i2) => acc + (i2 !== idx && r.cantidad !== '' ? Number(r.cantidad) : 0), 0);
                                            const maxDisponible = Math.max(0, producto.product_qt - sumaOtros);
                                            if (value !== '' && value > maxDisponible) {
                                              value = maxDisponible;
                                            }
                                            setCantidadNoEntregada(prev => {
                                              const nuevos = [...prev[producto.product_id]];
                                              nuevos[idx].cantidad = value;
                                              return { ...prev, [producto.product_id]: nuevos };
                                            });
                                          }}
                                          className="w-24 p-2 border rounded focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition"
                                          placeholder="Cantidad"
                                        />
                                        <select
                                          value={rechazo.motivo}
                                          onChange={e => {
                                            setCantidadNoEntregada(prev => {
                                              const nuevos = [...prev[producto.product_id]];
                                              nuevos[idx].motivo = e.target.value;
                                              return { ...prev, [producto.product_id]: nuevos };
                                            });
                                          }}
                                          className="w-56 p-2 border rounded focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition"
                                        >
                                          <option value="">Motivo</option>
                                          {motivos
                                            .filter(motivo => motivo.tipo === "RECHAZO PARCIAL")
                                            .map((motivo) => (
                                              <option key={motivo.id} value={motivo.descripcion}>{motivo.descripcion}</option>
                                            ))}
                                        </select>
                                        <button
                                          type="button"
                                          className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition text-xs font-medium"
                                          onClick={() => {
                                            setCantidadNoEntregada(prev => {
                                              const nuevos = [...prev[producto.product_id]];
                                              nuevos.splice(idx, 1);
                                              return { ...prev, [producto.product_id]: nuevos.length ? nuevos : [{ cantidad: '', motivo: '' }] };
                                            });
                                          }}
                                          disabled={cantidadNoEntregada[producto.product_id].length === 1}
                                          title="Eliminar rechazo"
                                        >
                                          <Minus size={16} />
                                          Eliminar
                                        </button>
                                      </div>
                                    ))}
                                    <button
                                      type="button"
                                      className="mt-2 px-3 py-1 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition text-xs font-medium flex items-center gap-1"
                                      onClick={() => {
                                        setCantidadNoEntregada(prev => ({
                                          ...prev,
                                          [producto.product_id]: [
                                            ...prev[producto.product_id],
                                            { cantidad: '', motivo: '' }
                                          ]
                                        }));
                                      }}
                                    >
                                      <Plus size={16} />
                                      Añadir motivo
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {valoresSelectores[orden.Order_number]?.Substatus === 'RECHAZADO' && (
                            <div className="mt-3">
                              <select
                                value={motivoNoEntregado[orden.Order_number] || ''}
                                onChange={(e) => {
                                  setMotivoNoEntregado((prev) => ({
                                    ...prev,
                                    [orden.Order_number]: e.target.value,
                                  }));
                                }}
                                className="w-full p-2 border rounded-md"
                              >
                                <option value="">Seleccione un motivo</option>
                                {motivos
                                  .filter(motivo => motivo.tipo === "RECHAZO TOTAL")
                                  .map((motivo) => (
                                    <option key={motivo.id} value={motivo.descripcion}>{motivo.descripcion}</option>
                                  ))}
                              </select>
                            </div>
                          )}
                        </li>
                      ))}
                  </ul>
                ) : (
                  <p className="text-gray-500">No hay órdenes asociadas.</p>
                )}
              </div>
            )}

            {currentStop !== null && (
              <div className="mt-6 flex justify-end">
                <button
                  onClick={confirmarCambios}
                  className="bg-green-600 text-white p-2 rounded-md hover:bg-green-700"
                >
                  Confirmar Cambios
                </button>
              </div>
            )}

            {embarque.status === 'FINALIZADO' && embarque.Substatus === 'RECHAZADO' && embarque.orders.some(order => order.substatus_reentrega === 'DESCARGANDO') && (
              <div className="mt-6 flex justify-end">
                <button
                  onClick={iniciarReentrega}
                  className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700"
                >
                  INICIAR REENTREGA
                </button>
              </div>
            )}
          </>
        )}

        {error && <p className="text-red-500 mt-2">{error}</p>}
        
      </div>
      <ToastContainer />

      {showReentregaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-auto relative">
            <button
              onClick={() => setShowReentregaModal(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
            <h2 className="text-lg font-semibold mb-4">Reentrega</h2>
            <ul className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {embarque.orders
                .filter((order) => order.substatus_reentrega === 'DESCARGANDO')
                .map((orden, index) => (
                  <li key={index} className="p-4 bg-white rounded-lg shadow-sm">
                    <div className="flex justify-between items-center">
                      <span className="font-medium"> {orden.Order_number}</span>
                      <select
                        value={valoresSelectores[orden.Order_number]?.Substatus || 'PENDIENTE'}
                        onChange={(e) =>
                          setValoresSelectores((prev) => ({
                            ...prev,
                            [orden.Order_number]: { Substatus: e.target.value },
                          }))
                        }
                        className="ml-2 p-2 border rounded-md"
                      >
                        <option value="PENDIENTE">PENDIENTE</option>
                        <option value="ENTREGADO">ENTREGADO</option>
                        <option value="PARCIAL">PARCIAL</option>
                        <option value="RECHAZADO">RECHAZADO</option>
                      </select>
                    </div>
                    {valoresSelectores[orden.Order_number]?.Substatus === 'PARCIAL' && (
                      <div className="mt-3 space-y-4">
                        {(orden.productos || []).map((producto, productIndex) => {
                          const productoDetalle = products.find(p => p.id === producto.product_id);
                          const codigoProducto = productoDetalle ? productoDetalle.codigo_producto : "Código no disponible";
                          const descripcionProducto = productoDetalle ? productoDetalle.descripcion : "Descripción no disponible";
                          if (!Array.isArray(cantidadNoEntregada[producto.product_id])) {
                            cantidadNoEntregada[producto.product_id] = [{ cantidad: '', motivo: '' }];
                          }
                          return (
                            <div key={productIndex} className="bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-200">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2 justify-between">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                  <span className="font-semibold text-blue-900">{codigoProducto}</span>
                                  <span className="text-gray-600 text-sm">{descripcionProducto}</span>
                                </div>
                                <span className="text-gray-500 text-xs sm:text-sm ml-auto">Total: {producto.product_qt}</span>
                              </div>
                              {(cantidadNoEntregada[producto.product_id] || []).map((rechazo, idx) => (
                                <div key={idx} className="flex flex-col sm:flex-row gap-2 items-center mb-2 bg-white rounded-md p-2 border border-blue-100">
                                  <input
                                    type="number"
                                    min="0"
                                    max={(() => {
                                      // Sumar todas las cantidades de rechazo para este producto, excepto la actual
                                      const rechazos = cantidadNoEntregada[producto.product_id] || [];
                                      const sumaOtros = rechazos.reduce((acc, r, i2) => acc + (i2 !== idx && r.cantidad !== '' ? Number(r.cantidad) : 0), 0);
                                      // El máximo permitido para este input es el total menos lo ya asignado en otros motivos
                                      return Math.max(0, producto.product_qt - sumaOtros);
                                    })()}
                                    value={rechazo.cantidad}
                                    onChange={e => {
                                      let value = e.target.value === '' ? '' : Number(e.target.value);
                                      // Sumar todas las cantidades de rechazo para este producto, excepto la actual
                                      const rechazos = cantidadNoEntregada[producto.product_id] || [];
                                      const sumaOtros = rechazos.reduce((acc, r, i2) => acc + (i2 !== idx && r.cantidad !== '' ? Number(r.cantidad) : 0), 0);
                                      const maxDisponible = Math.max(0, producto.product_qt - sumaOtros);
                                      if (value !== '' && value > maxDisponible) {
                                        value = maxDisponible;
                                      }
                                      setCantidadNoEntregada(prev => {
                                        const nuevos = [...prev[producto.product_id]];
                                        nuevos[idx].cantidad = value;
                                        return { ...prev, [producto.product_id]: nuevos };
                                      });
                                    }}
                                    className="w-24 p-2 border rounded focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition"
                                    placeholder="Cantidad"
                                  />
                                  <select
                                    value={rechazo.motivo}
                                    onChange={e => {
                                      setCantidadNoEntregada(prev => {
                                        const nuevos = [...prev[producto.product_id]];
                                        nuevos[idx].motivo = e.target.value;
                                        return { ...prev, [producto.product_id]: nuevos };
                                      });
                                    }}
                                    className="w-56 p-2 border rounded focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition"
                                  >
                                    <option value="">Motivo</option>
                                    {motivos
                                      .filter(motivo => motivo.tipo === "RECHAZO PARCIAL")
                                      .map((motivo) => (
                                        <option key={motivo.id} value={motivo.descripcion}>{motivo.descripcion}</option>
                                      ))}
                                  </select>
                                  <button
                                    type="button"
                                    className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition text-xs font-medium"
                                    onClick={() => {
                                      setCantidadNoEntregada(prev => {
                                        const nuevos = [...prev[producto.product_id]];
                                        nuevos.splice(idx, 1);
                                        return { ...prev, [producto.product_id]: nuevos.length ? nuevos : [{ cantidad: '', motivo: '' }] };
                                      });
                                    }}
                                    disabled={cantidadNoEntregada[producto.product_id].length === 1}
                                    title="Eliminar rechazo"
                                  >
                                    <Minus size={16} />
                                    Eliminar
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                className="mt-2 px-3 py-1 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition text-xs font-medium flex items-center gap-1"
                                onClick={() => {
                                  setCantidadNoEntregada(prev => ({
                                    ...prev,
                                    [producto.product_id]: [
                                      ...prev[producto.product_id],
                                      { cantidad: '', motivo: '' }
                                    ]
                                  }));
                                }}
                              >
                                <Plus size={16} />
                                Añadir rechazo
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {valoresSelectores[orden.Order_number]?.Substatus === 'RECHAZADO' && (
                      <div className="mt-3">
                        <select
                          value={motivoNoEntregado[orden.Order_number] || ''}
                          onChange={(e) => {
                            setMotivoNoEntregado((prev) => ({
                              ...prev,
                              [orden.Order_number]: e.target.value,
                            }));
                          }}
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="">Seleccione un motivo</option>
                          {motivos
                            .filter(motivo => motivo.tipo === "RECHAZO TOTAL")
                            .map((motivo) => (
                              <option key={motivo.id} value={motivo.descripcion}>{motivo.descripcion}</option>
                            ))}
                        </select>
                      </div>
                    )}
                  </li>
                ))}
            </ul>
            <div className="mt-6 flex justify-end">
              <button
                onClick={guardarReentrega}
                className="bg-green-600 text-white p-2 rounded-md hover:bg-green-700"
              >
                Guardar Reentrega
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModalEntrega;